<template>
  <StandardCardContainer>
    <v-tabs v-model="tab" class="topic-tabs mb-4">
      <v-tab value="overview" class="appkit-body">Overview</v-tab>
      <v-tab value="businessRelations" class="appkit-body"
        >Business relations
      </v-tab>
    </v-tabs>
    <v-expansion-panels variant="accordion" class="expandable-table" multiple>
      <v-expansion-panel
        v-for="tierGroup in dataAsList"
        :key="tierGroup.businessRelation"
        elevation="0"
        class="pl-0"
      >
        <v-expansion-panel-title>
          {{ tierGroup.businessRelation }}
          <CharacterInCircle
            >{{ tierGroup.recipients.length }}
          </CharacterInCircle>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div
            @mouseover="isHovering = true"
            :class="{ hover: isHovering }"
            style="overflow-x: scroll"
          >
            <o-table
              class="ma-2"
              :data="tierGroup.recipients"
              :paginated="true"
              :per-page="100"
              @click="emitContextId"
            >
              <o-table-column
                v-for="column in columns"
                v-bind="column"
                :visible="column.tabs.includes(tab)"
              >
                <template v-if="column.searchable" #searchable="props">
                  <o-input
                    v-model="props.filters[props.column.field]"
                    placeholder="Search..."
                    size="small"
                  />
                </template>
                <template #default="props">
                  <QuestionnaireStatusBadge
                    v-if="
                      column.field === 'forwardingStatus' ||
                      column.field === 'answerStatus'
                    "
                    class="text-center"
                    :status="props.row[column.field]"
                  />
                  <CountryText
                    v-else-if="column.field === 'countryCode'"
                    class="text-center"
                    :country-code="props.row[column.field]"
                    :include-country-name="false"
                    :display-tooltip="true"
                  />
                  <div
                    v-else-if="column.field === 'viewAnswers'"
                    class="text-center"
                  >
                    <AppkitIcon
                      v-if="
                        props.row.answerStatus === AnswerStatus.Completed
                      "
                      icon="icon-view-outline"
                      @click.stop.prevent="emitViewAnswers(props.row)"
                    />
                  </div>
                  <div
                    v-else-if="column.field === 'sentReminders'"
                    class="text-center"
                  >
                    {{ props.row[column.field] }}
                    <v-tooltip
                      v-if="props.row[column.field] != '0'"
                      activator="parent"
                      location="bottom"
                    >
                      <p
                        v-for="reminderDate in props.row['sentRemindersDates']"
                      >
                        {{ reminderDate }}
                      </p>
                    </v-tooltip>
                  </div>
                  <div v-else>
                    <p v-if="!(column.field === 'contextId')">
                      {{ props.row[column.field] }}
                    </p>
                  </div>
                </template>
              </o-table-column>
            </o-table>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </StandardCardContainer>
</template>

<script setup lang="ts">
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import { AnswerStatus } from "@/models/enum/answerStatus";
import { type GroupedReceivers } from "@/models/groupedReceivers";
import { ref, computed } from "vue";
import CharacterInCircle from "@/components/custom/CharacterInCircle.vue";
import CountryText from "@/components/custom/CountryText.vue";
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import QuestionnaireStatusBadge from "@/components/custom/QuestionnaireStatusBadge.vue";

const props = defineProps<{
  data: GroupedReceivers;
}>();

const emit = defineEmits<{
  (e: "unitSelected", contextId: string): void;
  (e: "viewAnswers", contextId: string, companyName: string): void;
}>();

const isHovering = ref(false);
const tab = ref("overview");

const dataAsList = computed(() => {
  let brs = Object.keys(props.data).sort();
  const index = brs.findIndex((x) => x == "Business partners");

  if (index !== -1) {
    brs.unshift(...brs.splice(index, 1));
  }

  return brs.map((br) => ({
    businessRelation: br,
    recipients: props.data[br],
  }));
});

const columns = ref([
  {
    field: "companyName",
    label: "Recipient",
    searchable: true,
    sortable: true,
    tabs: ["overview", "businessRelations"],
  },
  {
    field: "countryCode",
    label: "Country",
    searchable: true,
    sortable: true,
    tabs: ["overview", "businessRelations"],
  },
  {
    field: "lastEdited",
    label: "Last updated",
    searchable: true,
    sortable: true,
    tabs: ["overview"],
  },
  {
    field: "sentDate",
    label: "Sent",
    searchable: true,
    sortable: true,
    tabs: ["overview"],
  },
  {
    field: "dueDate",
    label: "Deadline",
    searchable: true,
    sortable: true,
    tabs: ["overview"],
  },
  {
    field: "sentReminders",
    label: "Reminders",
    searchable: true,
    sortable: true,
    tabs: ["overview"],
  },
  {
    field: "businessRelation",
    label: "Business relation",
    searchable: true,
    sortable: true,
    tabs: [],
  },
  {
    field: "senderCompanyNames",
    label: "Sender company",
    searchable: true,
    sortable: true,
    tabs: ["businessRelations"],
  },
  {
    field: "email",
    label: "Original recipient",
    searchable: true,
    sortable: true,
    tabs: ["businessRelations"],
  },
  {
    field: "activeUserEmail",
    label: "Active user",
    searchable: true,
    sortable: true,
    tabs: ["businessRelations"],
  },
  {
    field: "answerStatus",
    label: "Questionnaire",
    searchable: true,
    sortable: true,
    tabs: ["overview", "businessRelations"],
  },
  {
    field: "forwardingStatus",
    label: "Forwarding",
    tabs: ["overview", "businessRelations"],
  },
  {
    field: "viewAnswers",
    label: "View",
    tabs: ["overview", "businessRelations"],
  },
]);

function emitContextId(row: any) {
  emit("unitSelected", row.contextId);
}

function emitViewAnswers(row: any) {
  emit("viewAnswers", row.contextId, row.companyName);
}

function getTooltipText(answerStatus: AnswerStatus) {
  if (answerStatus === AnswerStatus.NotRegistered) {
    return "Not registered";
  }
  if (answerStatus === AnswerStatus.NotStarted) {
    return "Not started";
  }
  if (answerStatus === AnswerStatus.InProgress) {
    return "In progress";
  }
  if (answerStatus === AnswerStatus.Completed) {
    return "Completed";
  }
}
</script>

<style scoped lang="scss">
.hover {
  cursor: pointer;
}

.expandable-table {
  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 !important;
  }

  :deep(.v-expansion-panel-title) {
    background-color: var(--light-grey-4);
  }
}

.topic-tabs {
  :deep(.v-tab--selected) {
    color: var(--orange) !important;
  }
}
</style>
